import { inject, Injectable } from '@angular/core';
import { ActualServiceFilterByControlTowerIdFilterInput } from '@control-tower/features/task-feed/queries/find-actual-services-by-control-tower.query';
import { GraphqlClientService } from '@core/services/graphql-client.service';
import { ActualService } from '../models/actual-service.model';
import { CollaboratorInvitation } from '../models/collaborator-invitation.model';
import { Collaborator } from '../models/collaborator.model';
import { InviteMultipleCollaboratorsInput } from '../models/invite-multiple-collaborators.input';
import { InviteMultipleGuestCollaboratorsInput } from '../models/invite-multiple-guest-collaborators.input';
import { PermissionNodeInput } from '../models/permission-node.input';
import {
  CANCEL_COLLABORATOR_INVITATION,
  DELETE_COLLABORATOR_INVITATION,
  FIND_ACTUAL_SERVICES_BY_CONTROL_TOWER_ID,
  FIND_ACTUAL_SERVICES_BY_CONTROL_TOWER_ID_WITH_STATUS_COUNTS_QUERY,
  FIND_ORG_USER_WITH_SCOPES_BY_INDIVIDUAL_ID,
  GET_COLLABORATOR_BY_USER_ID_AND_CONTROL_TOWER_ID,
  GET_COLLABORATOR_INVITATIONS,
  GET_COLLABORATORS_BY_CONTROL_TOWER_ID,
  GET_COLLABORATORS_BY_CONTROL_TOWER_ID_WITH_PAGINATION,
  GET_EXTERNAL_USERS,
  GET_INTERNAL_USERS,
  INVITE_MULTIPLE_COLLABORATORS,
  INVITE_MULTIPLE_GUEST_COLLABORATORS,
  IS_COLLABORATOR_OF_CONTROL_TOWER_BY_USER_ID_AND_CONTROL_TOWER_ID,
  REMOVE_COLLABORATOR,
  RESEND_COLLABORATOR_INVITATION,
  UPDATE_COLLABORATOR_FEATURE_MARKETING_META_DATA,
} from '../queries/collaborator.queries';

@Injectable({
  providedIn: 'root',
})
export class CollaboratorsService {
  gqlClient = inject(GraphqlClientService);

  getCurrentCollaborators(controlTowerId: string) {
    return this.gqlClient.query<{ getCollaboratorsByControlTowerId: Collaborator[] }, { controlTowerId: string }>(
      GET_COLLABORATORS_BY_CONTROL_TOWER_ID,
      {
        controlTowerId,
      }
    );
  }

  getCurrentCollaboratorsWithPagination(
    controlTowerId: string,
    offset: number = 0,
    limit: number = 10,
    searchKey?: string,
    collaboratorType?: string,
    role?: string,
    verified?: boolean,
    sortBy: string = 'createdAt',
    sortOrder: 'asc' | 'desc' = 'desc',
    sortByProfilePhotosFirst: boolean = false
  ) {
    return this.gqlClient.query<
      {
        getCollaboratorsByControlTowerIdWithPagination: {
          items: Collaborator[];
          pageInfo: { hasNextPage: boolean; hasPreviousPage: boolean; pageSize: number; total: number };
        };
      },
      {
        input: {
          controlTowerId: string;
          offset: number;
          limit: number;
          searchKey?: string;
          collaboratorType?: string;
          role?: string;
          verified?: boolean;
          sortBy: string;
          sortOrder: string;
          sortByProfilePhotosFirst: boolean;
        };
      }
    >(GET_COLLABORATORS_BY_CONTROL_TOWER_ID_WITH_PAGINATION, {
      input: {
        controlTowerId,
        offset,
        limit,
        ...(searchKey && { searchKey }),
        ...(collaboratorType && { collaboratorType }),
        ...(role && { role }),
        ...(verified !== undefined && { verified }),
        sortBy,
        sortOrder,
        sortByProfilePhotosFirst,
      },
    });
  }

  getCollaboratorByUserIdAndControlTowerId(userId: string, controlTowerId: string) {
    return this.gqlClient.query<
      { getCollaboratorByUserIdAndControlTowerId: Collaborator },
      { userId: string; controlTowerId: string }
    >(GET_COLLABORATOR_BY_USER_ID_AND_CONTROL_TOWER_ID, {
      userId,
      controlTowerId,
    });
  }

  isCollaboratorOfControlTowerByUserIdAndControlTowerId(userId: string, controlTowerId: string) {
    return this.gqlClient.query<
      { isCollaboratorOfControlTowerByUserIdAndControlTowerId: boolean },
      { userId: string; controlTowerId: string }
    >(IS_COLLABORATOR_OF_CONTROL_TOWER_BY_USER_ID_AND_CONTROL_TOWER_ID, {
      userId,
      controlTowerId,
    });
  }

  getInternalUsers(orgId: string, searchText = '', email = '') {
    return this.gqlClient.query<
      { getUsersByOrgIdWithFilters: Collaborator[] },
      { getUserByOrgIdInput: { orgId: string; searchText?: string; email?: string } }
    >(GET_INTERNAL_USERS, {
      getUserByOrgIdInput: {
        orgId,
        ...(searchText && { searchText }),
        ...(email && { email }),
      },
    });
  }

  getExternalUsers(orgId: string, searchText = '', email = '', limit = 20, offset = 0) {
    return this.gqlClient.query<
      {
        getUsersNotRelatedToOrgIdWithFilters: {
          items: Collaborator[];
          pageInfo: { hasNextPage: boolean; hasPreviousPage: boolean; pageSize: number; total: number };
        };
      },
      {
        getUserByOrgIdInput: { orgId: string; searchText: string; email: string };
        limit: number;
        offset: number;
        searchKey: string;
      }
    >(GET_EXTERNAL_USERS, {
      getUserByOrgIdInput: {
        orgId,
        ...(searchText && { searchText }),
        ...(email && { email }),
      },
      limit,
      offset,
      searchKey: '',
    });
  }

  removeCollaborator(collaboratorId: string) {
    return this.gqlClient.mutation<
      { removeCollaborator: Collaborator },
      { removeCollaboratorInput: { collaboratorId: string } }
    >(REMOVE_COLLABORATOR, { removeCollaboratorInput: { collaboratorId } });
  }

  getPendingInvitations(controlTowerId?: string, status?: string) {
    return this.gqlClient.query<
      { getCollaboratorInvitations: CollaboratorInvitation[] },
      { controlTowerId?: string; status?: string }
    >(GET_COLLABORATOR_INVITATIONS, {
      ...(controlTowerId && { controlTowerId }),
      ...(status && { status }),
    });
  }

  cancelCollaboratorInvitation(invitationId: string) {
    return this.gqlClient.mutation<{ cancelCollaboratorInvitation: CollaboratorInvitation }, { invitationId: string }>(
      CANCEL_COLLABORATOR_INVITATION,
      { invitationId }
    );
  }

  deleteCollaboratorInvitation(invitationId: string) {
    return this.gqlClient.mutation<{ deleteCollaboratorInvitation: CollaboratorInvitation }, { invitationId: string }>(
      DELETE_COLLABORATOR_INVITATION,
      { invitationId }
    );
  }

  resendCollaboratorInvitation(invitationId: string) {
    return this.gqlClient.mutation<{ resendCollaboratorInvitation: CollaboratorInvitation }, { invitationId: string }>(
      RESEND_COLLABORATOR_INVITATION,
      { invitationId }
    );
  }

  findActualServicesByControlTowerId(controlTowerId: string) {
    return this.gqlClient.query<{ findActualServicesByControlTowerId: ActualService[] }, { controlTowerId: string }>(
      FIND_ACTUAL_SERVICES_BY_CONTROL_TOWER_ID,
      {
        controlTowerId,
      }
    );
  }

  inviteMultipleCollaborators(
    userIds: string[],
    collaboratorType: string,
    orgId: string,
    controlTowerId: string,
    permissionNodes: PermissionNodeInput[] = [],
    permissionScope?: string,
    actualServiceIds: string[] = [],
    userPermissions?: { userId: string; permissionNodes: PermissionNodeInput[]; saveAsDefault?: boolean }[]
  ) {
    return this.gqlClient.mutation<
      { inviteMultipleCollaborators: CollaboratorInvitation[] },
      { inviteMultipleCollaboratorsInput: InviteMultipleCollaboratorsInput }
    >(INVITE_MULTIPLE_COLLABORATORS, {
      inviteMultipleCollaboratorsInput: {
        userIds,
        collaboratorType,
        orgId,
        controlTowerId,
        permissionNodes,
        permissionScope,
        actualServiceIds,
        ...(userPermissions && { userPermissions }),
      },
    });
  }

  inviteMultipleGuestCollaborators(guestCollaboratorsInput: InviteMultipleGuestCollaboratorsInput) {
    return this.gqlClient.mutation<
      { inviteMultipleGuestCollaborators: CollaboratorInvitation[] },
      { inviteMultipleGuestCollaboratorsInput: InviteMultipleGuestCollaboratorsInput }
    >(INVITE_MULTIPLE_GUEST_COLLABORATORS, {
      inviteMultipleGuestCollaboratorsInput: guestCollaboratorsInput,
    });
  }

  getAvailableServiceTypes(controlTowerId: string) {
    const filter: ActualServiceFilterByControlTowerIdFilterInput = {
      controlTowerId,
      status: undefined,
      searchKey: '',
    };
    return this.gqlClient.query<
      { actualServicesByControlTowerIdOrParentControlTowerIdWithStatusCounts: { services: ActualService[] } },
      { filter: ActualServiceFilterByControlTowerIdFilterInput }
    >(FIND_ACTUAL_SERVICES_BY_CONTROL_TOWER_ID_WITH_STATUS_COUNTS_QUERY, {
      filter,
    });
  }

  findOrgUserWithScopesByIndividualId(individualId: string) {
    return this.gqlClient.query<{ getControlTowerOrgUserWithScopesByIndividualId: string[] }, { individualId: string }>(
      FIND_ORG_USER_WITH_SCOPES_BY_INDIVIDUAL_ID,
      {
        individualId,
      }
    );
  }

  updateCollaboratorFeatureMarketingMetaData(
    collaboratorId: string,
    featureMarketingMetaData: {
      isCTAcompleted: boolean;
    }
  ) {
    return this.gqlClient.mutation<
      { updateCollaboratorFeatureMarketingMetaData: Collaborator },
      { collaboratorId: string; featureMarketingMetaData: { isCTAcompleted: boolean } }
    >(UPDATE_COLLABORATOR_FEATURE_MARKETING_META_DATA, {
      collaboratorId,
      featureMarketingMetaData,
    });
  }
}
